import { z } from 'zod';
import { IsoDate, IsoDateTime } from './common.js';

export const TimeRangeSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/).describe('HH:MM local'),
  end: z.string().regex(/^\d{2}:\d{2}$/),
  slotMinutes: z.number().int().min(5).max(240).default(15),
  appointmentTypeCodes: z.array(z.string()).optional().describe('Restrict to these types; omit for any'),
  location: z.string().optional(),
});

/** Weekly availability template per provider. Weekday 0 = Sunday (Saudi working week starts Sunday). */
export const ProviderScheduleSchema = z.object({
  tenantId: z.string(),
  providerId: z.string(),
  timeZone: z.string(),
  weekly: z.record(z.string().regex(/^[0-6]$/), z.array(TimeRangeSchema)).default({}),
  effectiveFrom: IsoDate.optional(),
  effectiveTo: IsoDate.optional(),
  updatedAt: IsoDateTime,
});
export type ProviderSchedule = z.infer<typeof ProviderScheduleSchema>;

export const ScheduleExceptionSchema = z.object({
  tenantId: z.string(),
  providerId: z.string(),
  date: IsoDate,
  /** Empty ranges means the day is blocked (leave, holiday). */
  ranges: z.array(TimeRangeSchema),
  reason: z.string().optional(),
});
export type ScheduleException = z.infer<typeof ScheduleExceptionSchema>;

export const AppointmentStatusSchema = z.enum([
  'PROPOSED',    // physician suggested a follow-up; no slot held
  'DRAFT',       // slot tentatively chosen, awaiting confirmation (pending action)
  'BOOKED',
  'CHECKED_IN',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const AppointmentSchema = z.object({
  tenantId: z.string(),
  appointmentId: z.string(),
  patientId: z.string(),
  providerId: z.string(),
  appointmentTypeCode: z.string(),
  /** UTC instant of the slot start. */
  start: IsoDateTime,
  end: IsoDateTime,
  /** Local calendar date in the tenant time zone, used for daily listings. */
  localDate: IsoDate,
  status: AppointmentStatusSchema,
  reason: z.string().optional(),
  notes: z.string().optional(),
  channel: z.enum(['FRONT_DESK', 'PHYSICIAN', 'PATIENT_PORTAL', 'SYSTEM']),
  createdByUserId: z.string(),
  encounterId: z.string().optional(),
  reminderSentAt: IsoDateTime.optional(),
  checkedInAt: IsoDateTime.optional(),
  cancelledAt: IsoDateTime.optional(),
  cancellationReason: z.string().optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  version: z.number().int().default(1),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const SlotSchema = z.object({
  providerId: z.string(),
  providerName: z.string().optional(),
  start: IsoDateTime,
  end: IsoDateTime,
  localDate: IsoDate,
  localTime: z.string().describe('HH:MM in tenant time zone'),
  appointmentTypeCode: z.string(),
});
export type Slot = z.infer<typeof SlotSchema>;

export const FollowUpRequestSchema = z.object({
  tenantId: z.string(),
  followUpId: z.string(),
  patientId: z.string(),
  requestedByUserId: z.string(),
  providerId: z.string().optional(),
  encounterId: z.string().optional(),
  appointmentTypeCode: z.string(),
  /** Earliest acceptable date, e.g. "in three months" resolved to a date. */
  targetDate: IsoDate,
  windowDays: z.number().int().min(0).max(90).default(14),
  reason: z.string().optional(),
  status: z.enum(['OPEN', 'BOOKED', 'CANCELLED']),
  appointmentId: z.string().optional(),
  createdAt: IsoDateTime,
});
export type FollowUpRequest = z.infer<typeof FollowUpRequestSchema>;

export const WaitlistEntrySchema = z.object({
  tenantId: z.string(),
  waitlistId: z.string(),
  patientId: z.string(),
  providerId: z.string().optional(),
  appointmentTypeCode: z.string(),
  earliest: IsoDate,
  latest: IsoDate.optional(),
  priority: z.number().int().min(0).max(10).default(5),
  status: z.enum(['WAITING', 'OFFERED', 'BOOKED', 'CANCELLED']),
  createdAt: IsoDateTime,
});
export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>;
